import { useQuery } from "@tanstack/react-query";
import { toZonedTime, format as tzFormat } from "date-fns-tz";
import { format } from "path-browserify";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ReactGA from "react-ga4";
import { getVirtualCoursesByUserId } from "../../services/dashboardService";
import { api1 } from "../../utils/axiosInstance";
import FilterButton from "../buttons/FilterButtons";
import VirtualCourseCard from "../virtualCourses/virtualCourseCard";
import WelcomeCard from "../WelcomeCard";
import UpskillingPathway from "../learningPathway/UpskillingPathway";
import { AlertCircle } from "lucide-react";
import SecondaryLoader from "../SecondaryLoader/SecondaryLoader";

const AllCourses = ({ initialTarget = false }) => {
  const filters = ["All", "Started", "In Progress", "Completed"];
  const [filteredCourses, setFilteredCourses] = useState();
  const [selectedFilter, setSelectedFilter] = useState("All"); // Track selected filter

  const [myCourses, setMyCourses] = useState([]);
  const [bookmarkedCourseIds, setBookmarkedCourseIds] = useState(new Set());

  const { currentJob, targetJob } = useSelector((state) => state.user);
  const userId = useSelector((state) => state.auth.id);

  useEffect(() => {
    const fetchBookmarkedCourses = async () => {
      try {
        const response = await api1.get(
          `/api/courses/get-bookmark-courses/${userId}`
        );
        const bookmarkedCourses = response.data.data;
        setMyCourses(bookmarkedCourses);
        setFilteredCourses(bookmarkedCourses); //this needs to be updated when I change the status of the course

        const bookmarkedIds = new Set(
          bookmarkedCourses.map((course) => course.courseId)
        );
        setBookmarkedCourseIds(bookmarkedIds);
      } catch (error) {
        console.error("Error fetching bookmarks", error);
      }
    };

    fetchBookmarkedCourses();
  }, [userId]);

  const handleBookmarkToggle = (course, isCurrentlyBookmarked) => {
    // Update bookmarkedCourseIds Set
    const newBookmarkedIds = new Set(bookmarkedCourseIds);

    if (isCurrentlyBookmarked) {
      newBookmarkedIds.delete(course.courseId);
      // Remove from myCourses if unbookmarked
      setMyCourses((prevCourses) =>
        prevCourses.filter((c) => c.courseId !== course.courseId)
      );
    } else {
      newBookmarkedIds.add(course.courseId);
      // Add to myCourses if bookmarked
      setMyCourses((prevCourses) => [...prevCourses, course]);
    }

    setBookmarkedCourseIds(newBookmarkedIds);
  };

  const handleClick = (url, courseTitle, partner) => {
    // Track external link click with course info
    const isCoursera = url?.toLowerCase().includes('coursera.org');
    ReactGA.event(isCoursera ? 'coursera_click' : 'click', {
      link_url: url,
      link_domain: new URL(url).hostname,
      link_text: courseTitle,
      course_title: courseTitle,
      partner: partner || (isCoursera ? 'Coursera' : undefined),
      outbound: true
    });
    window.open(url, "_blank");
  };

  const handleFilterSelect = (selectedFilter) => {
    setSelectedFilter(selectedFilter); // Store selected filter state
    if (selectedFilter === "All") {
      setFilteredCourses(myCourses);
    } else {
      const filtered = myCourses.filter(
        (course) =>
          course?.status?.toLowerCase() === selectedFilter.toLowerCase()
      );
      setFilteredCourses(filtered);
    }
  };

  useEffect(() => {
    handleFilterSelect(selectedFilter); // Reapply the current filter
  }, [myCourses]); // When myCourses update, refilter them

  const handleStatusChange = (courseId, newStatus) => {
    setMyCourses((prevCourses) =>
      prevCourses.map((course) =>
        course.courseId === courseId ? { ...course, status: newStatus } : course
      )
    );

    // Reapply the filter after updating the status
    handleFilterSelect(selectedFilter);
  };

  const {
    data: fetchVirtualCourses,
    isLoading,
    error: fetchVirtualCoursesError,
  } = useQuery({
    queryKey: ["virtualCoursesByUserId", userId],
    queryFn: () => getVirtualCoursesByUserId(userId),
    enabled: !!userId,
  });

  const formatCourseData = (courseList) => {
    if (!courseList) return [];
    return courseList.map((course) => {
      const courseDateUTC = new Date(course.date);
      const courseTimeInTZ = toZonedTime(courseDateUTC, course.timezone);

      return {
        id: course._id,
        name: course.courseName,
        description: course.courseDescription,
        instructor: course.instructorName.name,
        date: format(courseTimeInTZ, "MMMM d, yyyy"),
        time: tzFormat(courseTimeInTZ, "hh:mm a", {
          timeZone: course.timezone,
        }),
        timezone: course.timezone,
        price: course.price,
        seatsAvailable: course.seats,
        duration: course.duration,
        registeredUsers: course.registeredUsers,
      };
    });
  };

  const courses = formatCourseData(fetchVirtualCourses);

  return (
    <div className="flex flex-col gap-6">
      <WelcomeCard
        title="My Learning Pathway"
        current={currentJob}
        target={targetJob}
        // description="Follow a structured, step-by-step plan to build essential skills for your chosen career. Track your progress, enroll in recommended courses, and achieve your career goals with confidence."
      />
      <div className="flex justify-between items-center">
        <p className="font-bold text-3xl text-custom-pink">
          My Selected Career - {targetJob}
        </p>
        <FilterButton filters={filters} onSelect={handleFilterSelect} />
      </div>
      <div className="bg-white px-4 sm:px-5 py-6 shadow-md rounded-xl">
        <p className="text-custom-blue font-bold text-lg sm:text-xl md:text-2xl">
          My Instructor-Led Virtual Courses
        </p>
        {isLoading ? (
          <SecondaryLoader />
        ) : fetchVirtualCoursesError ? (
          <div className="flex flex-col items-center justify-center p-6 text-red-500">
            <AlertCircle className="w-8 h-8 mb-2" />
            <p>Error loading courses. Please try again later.</p>
          </div>
        ) : courses.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-6 text-gray-500">
            <AlertCircle className="w-8 h-8 mb-2" />
            <p>You haven't registered to any course</p>
          </div>
        ) : (
          <VirtualCourseCard courses={courses} onClick={(url) => handleClick(url, null, null)} />
        )}
      </div>

      <UpskillingPathway
        title={"My Self-Learning Courses"}
        learningPathwayList={filteredCourses}
        onClick={(url, course) => handleClick(url, course?.name || course?.title, course?.partners?.[0])}
        bookmarkedCourseIds={bookmarkedCourseIds}
        onBookmarkToggle={handleBookmarkToggle}
        onStatusChange={handleStatusChange}
      />
    </div>
    
  );
};

export default AllCourses;
